// Self-serve subscription plans for the Agency Tools product.
//
// Renders on the Lead-Gen Tools marketing landing page, right before the
// existing "Talk to us" PricingTease section. The 4 self-serve tiers
// (Free / Starter / Growth / Scale) get a card each + Enterprise card
// with a "Contact us" CTA. Monthly ↔ Yearly toggle drives the price + a
// "2 months free" savings badge on yearly cards.
//
// Pricing is authoritative — must mirror
// `supabase/functions/_shared/razorpay-agency-plans.ts` (the server-side
// source of truth for what Razorpay actually charges).

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { openEmbedInquiry } from './openEmbedInquiry';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { stashPricingIntent } from './pricingIntent';
import {
  type Cycle,
  type TierCard,
  YEARLY_ENABLED,
  TIERS,
  formatPrice,
} from './agencyTiers';

// Public marketing pages render outside the AuthProvider tree, so we read
// the session straight from the supabase client instead of useAuth(). For
// sign-in we call signInWithOAuth directly — the public page only ever
// runs on the main domain (not on whitelabel agency subdomains), so the
// agency-domain branch of AuthProvider.signInWithGoogle is not relevant here.

export function AgencyPricingPlans({ showHeader = true, showTrialCta = true }: { showHeader?: boolean; showTrialCta?: boolean } = {}) {
  const [cycle, setCycle] = useState<Cycle>('monthly');
  const navigate = useNavigate();
  const [isSignedIn, setIsSignedIn] = useState(false);

  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(({ data }) => {
      if (!cancelled) setIsSignedIn(!!data.session?.user);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsSignedIn(!!session?.user);
    });
    return () => { cancelled = true; subscription.unsubscribe(); };
  }, []);

  const startGoogleOAuth = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/fly-app`,
        queryParams: { access_type: 'offline', prompt: 'select_account' },
        scopes: 'email profile https://www.googleapis.com/auth/business.manage',
      },
    });
    if (error) console.error('Google sign-in failed:', error);
  };

  // Re-read the live session before deciding to OAuth. The `isSignedIn`
  // state can lag the real session (initial getSession still in flight, a
  // transient onAuthStateChange null, or a fast click), which would bounce
  // an already signed-in visitor through Google again instead of taking
  // them straight to checkout. getSession reads the persisted session
  // locally, so this is cheap.
  const resolveSignedIn = async (): Promise<boolean> => {
    if (isSignedIn) return true;
    const { data } = await supabase.auth.getSession();
    return !!data.session?.user;
  };

  type BusyKey = TierCard['slug'] | 'trial';
  const [busyTier, setBusyTier] = useState<BusyKey | null>(null);

  // Routes the user into the 7-day free trial flow. The trial is no
  // longer a separate "Free" tile — it lives as a secondary CTA on the
  // Starter card. Signed-in users get their lead-gen agency provisioned
  // (the bootstrap edge function is idempotent for returning users) and
  // land on the lead-gen tools dashboard. Signed-out users stash the
  // intent + start Google OAuth; AuthProvider on /fly-app consumes the
  // intent on return and runs bootstrapAndRedirectForIntent which
  // ultimately lands them on the same /lead-gen-tools?welcome=free URL.
  const onTrialClick = async () => {
    if (await resolveSignedIn()) {
      setBusyTier('trial');
      try {
        // intent='free' — this is the explicit "Try free for 7 days" CTA
        // path, so the auto-trial subscription must survive bootstrap.
        await supabase.functions.invoke('agency-public-signup-bootstrap', { body: { intent: 'free' } });
      } catch (err) {
        console.warn('Bootstrap failed; AuthProvider will retry:', err);
      }
      setBusyTier(null);
      navigate('/fly-app/lead-gen-tools?welcome=free');
      return;
    }
    stashPricingIntent({ kind: 'free' });
    startGoogleOAuth();
  };

  const onCtaClick = async (tier: TierCard) => {
    if (await resolveSignedIn()) {
      // Paid: bootstrap to get the agency id, then navigate with it baked in.
      // intent='paid' — strips the auto-trial so the buyer reaches checkout
      // with no active subscription. Razorpay's mandate flow creates the
      // legit trial-then-paid subscription after card authentication.
      setBusyTier(tier.slug);
      const { data, error } = await supabase.functions.invoke('agency-public-signup-bootstrap', { body: { intent: 'paid' } });
      setBusyTier(null);
      if (error || !data?.agency_id) {
        console.error('Bootstrap failed:', error, data);
        // Fall back to opening the inquiry modal so the user has a path forward.
        openEmbedInquiry(`pricing-${tier.slug}-bootstrap-failed`, 'talk');
        return;
      }
      const qs = new URLSearchParams({
        plan: tier.slug,
        cycle,
        agency: data.agency_id as string,
        source: `pricing-${tier.slug}-${cycle}`,
      });
      navigate(`/fly-app/agency-checkout?${qs.toString()}`);
      return;
    }
    // Signed out: stash the intent + start Google OAuth. AuthProvider on
    // /fly-app consumes the intent on return and routes us correctly.
    stashPricingIntent({ kind: 'paid', plan: tier.slug as 'starter' | 'growth' | 'scale', cycle });
    startGoogleOAuth();
  };

  return (
    <section className="relative bg-gradient-to-b from-[#0d0d14] to-gray-950 py-32 lg:py-40 overflow-hidden">
      {/* Subtle gradient backdrop for depth */}
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at top, rgba(139, 92, 246, 0.15), transparent 50%), radial-gradient(ellipse at bottom right, rgba(236, 72, 153, 0.08), transparent 50%)',
        }}
      />

      <div className="relative container mx-auto px-6 lg:px-8">
        <motion.div
          id="pricing"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto text-center mb-12 scroll-mt-28"
        >
          {showHeader && (
            <>
              <div className="text-xs uppercase tracking-[0.2em] text-fuchsia-400 font-semibold mb-4">
                Self-serve plans
              </div>
              <h2
                className="text-white font-bold tracking-tight"
                style={{ fontSize: 'clamp(2rem, 4vw, 3.4rem)', lineHeight: 1.1 }}
              >
                Pricing that scales with your lead flow.
              </h2>
              <p className="mt-5 text-white/60 text-lg">
                Pay only for credits you consume.{YEARLY_ENABLED && ' Yearly billing gets you 2 months free.'} Cancel anytime.
              </p>
            </>
          )}

          {/* Monthly / Yearly toggle (hidden while YEARLY_ENABLED is false) */}
          {YEARLY_ENABLED && (
          <div className="mt-8 inline-flex items-center rounded-full border border-white/15 bg-white/[0.04] p-1">
            <button
              type="button"
              onClick={() => setCycle('monthly')}
              className={`px-5 py-2 text-sm rounded-full transition ${
                cycle === 'monthly'
                  ? 'bg-white text-gray-900 font-semibold'
                  : 'text-white/70 hover:text-white'
              }`}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setCycle('yearly')}
              className={`relative px-5 py-2 text-sm rounded-full transition ${
                cycle === 'yearly'
                  ? 'bg-white text-gray-900 font-semibold'
                  : 'text-white/70 hover:text-white'
              }`}
            >
              Yearly
              <span className="ml-1.5 inline-flex items-center gap-1 rounded-full bg-emerald-500/20 text-emerald-300 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5">
                2 mo free
              </span>
            </button>
          </div>
          )}
        </motion.div>

        {/* Four tiers (Starter / Growth / Scale / Enterprise) on a
            4-column grid. The standalone "Free / 7-day trial" tile has
            been collapsed onto Starter via the trialCtaLabel field —
            buyers see "Try free for 7 days" as a secondary CTA on the
            Starter card instead of a phantom Free plan no one
            subscribes to. */}
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3 max-w-5xl mx-auto items-stretch">
          {TIERS.map((t, i) => {
            // Yearly paid cards lead with the per-month equivalent (smaller
            // headline number) and disclose the true annual charge below.
            const headline =
              cycle === 'monthly' ? t.monthly_usd
              : t.yearly_usd != null ? Math.round(t.yearly_usd / 12) : null;
            const showYearlyNote = cycle === 'yearly' && t.yearly_usd != null;
            return (
              <motion.div
                key={t.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.55, delay: i * 0.06 }}
                className={`relative ${t.highlight ? 'lg:-my-3 z-10' : ''}`}
              >
                {t.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10 px-3 py-1 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white text-[10px] font-bold uppercase tracking-wider shadow-lg shadow-fuchsia-500/40">
                    Most popular
                  </div>
                )}
                <div
                  className={`h-full rounded-2xl border p-6 flex flex-col items-center text-center transition-all duration-300 hover:-translate-y-1 ${
                    t.highlight
                      ? 'border-fuchsia-500/50 bg-gradient-to-br from-fuchsia-500/[0.12] via-[#15131f] to-[#0c0c14] shadow-2xl shadow-fuchsia-500/20 ring-1 ring-fuchsia-400/20 lg:p-7'
                      : 'border-white/10 bg-gradient-to-br from-[#13131c] to-[#0c0c14] hover:border-white/20'
                  }`}
                >
                  <div className="mb-5">
                    <h3 className="text-white text-xl font-bold mb-1">{t.name}</h3>
                    <p className="text-white/55 text-sm leading-snug">{t.tagline}</p>
                  </div>

                  <div className="mb-5 min-h-[60px]">
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-white text-4xl font-bold tabular-nums">{formatPrice(headline)}</span>
                      <span className="text-white/55 text-sm">/mo</span>
                    </div>
                    {showYearlyNote && (
                      <div className="text-emerald-300/90 text-xs mt-1.5">
                        billed yearly at ${t.yearly_usd}
                      </div>
                    )}
                  </div>

                  {/* Highlights — single-source-of-truth list from agencyTiers.ts,
                      identical to what the Product Hunt landing page promises so
                      the marketing + checkout copy never drifts. First bullet
                      gets the sparkle (credits headline); the rest get checks. */}
                  <ul className="space-y-2.5 mb-6 flex-1 w-full">
                    {t.highlights.map((h, idx) => (
                      <li
                        key={h}
                        className="flex items-start justify-center gap-2 text-sm text-white/85"
                      >
                        {idx === 0 ? (
                          <Sparkles className="mt-0.5 size-3.5 text-violet-300 shrink-0" />
                        ) : (
                          <Check className="mt-0.5 size-3.5 text-emerald-400 shrink-0" />
                        )}
                        <span className="text-left">{h}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="space-y-2 mt-auto w-full">
                    <Button
                      onClick={() => onCtaClick(t)}
                      disabled={busyTier === t.slug || busyTier === 'trial'}
                      className={`w-full h-11 rounded-xl font-semibold ${
                        t.highlight
                          ? 'bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white'
                          : 'bg-white/90 hover:bg-white text-gray-900'
                      }`}
                    >
                      {busyTier === t.slug ? 'Setting up...' : t.ctaLabel}
                      <ArrowRight className="ml-1.5 size-4" />
                    </Button>
                    {/* Secondary trial CTA. Only Starter carries this
                        today — the trial is "try Starter free for 7
                        days" semantically. Renders as an outlined
                        button under the primary so the buyer sees both
                        the commit path AND the no-risk path side by
                        side without a second tile. */}
                    {showTrialCta && t.trialCtaLabel && (
                      <Button
                        type="button"
                        onClick={onTrialClick}
                        disabled={busyTier === t.slug || busyTier === 'trial'}
                        variant="outline"
                        className="w-full h-10 rounded-xl font-medium bg-transparent border border-white/20 text-white/85 hover:bg-white/10 hover:text-white hover:border-white/35"
                      >
                        {busyTier === 'trial' ? 'Setting up...' : t.trialCtaLabel}
                      </Button>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <p className="mt-10 text-center text-xs text-white/45 max-w-2xl mx-auto leading-relaxed">
          Prices shown in USD. Indian agencies are billed in INR at a locked
          ₹83/$1 with a GST-compliant tax invoice. Top-up packs (+1,000 / +2,500 / +7,500 / +20,000 / +50,000 credits)
          available on every paid plan and never expire.
        </p>
      </div>
    </section>
  );
}
